import React, { useState, useEffect, useLayoutEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMessageQuestion } from '@fortawesome/pro-solid-svg-icons';
import { faCircleDollar } from '@fortawesome/pro-solid-svg-icons';
import { faFilePdf, faCircle1, faCircle2, faCircle3, faGraduationCap, faUserHelmetSafety, faBuildingColumns, faSquareQuestion } from '@fortawesome/pro-duotone-svg-icons';
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import HeaderComponent from './HeaderComponent';
import FeatureNav from './FeatureNav';
import BreadCrumbs from './BreadCrumbs';
import Loader from './Loader';
import FooterSection from './FooterSection';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import 'bootstrap-select/dist/css/bootstrap-select.min.css';
import 'bootstrap-select';
import $ from 'jquery';
import {
  initLegacyCharts,
  debounce,
  build_sources,
  cartesianprod,
  snake_caseify,
  format_date_snakecase,
  getContrastYIQ
} from '../assets/js/legacy/higherEd';
import LectureImage from "../assets/images/lecture-1920_960.jpg";
import { SingleSelect } from './SingleSelect';
import '../assets/styles/higher_ed.css';
import { MultiSelectNoExtras } from './MultiSelectNoExtras';

declare global {
  interface Window {
    selectedEntities?: string[];
    selectedYears?: string[];
    build_expenditure_by_institution_barchart?: (entities: string[], years: number[]) => void;
    build_expenditure_by_institution_table?: (entities: string[], years: number[]) => void;
    build_fte_by_institution_barchart?: (entities: string[], years: number[]) => void;
    build_fte_table?: (entities: string[], years: number[]) => void;
    build_detail_expenditure_by_institution_table?: (entities: string[], years: number[]) => void;
    build_institution_exp_barchart?: (entities: string[], years: number[]) => void;
  }
}

const HigherEducation = () => {
  const [selectedEntities, setSelectedEntities] = useState<string[]>([
    "University of Utah"
  ]);

  const [selectedYears, setSelectedYears] = useState<string[]>([
    "2014", "2015", "2016", "2017", "2018", "2019", "2020", "2021", "2022", "2023", "2024"
  ])

  const technicalColleges = [
    "Davis Technical College", "Dixie Technical College", "Mountainland Technical College", "Ogden-Weber Technical College", "Salt Lake Community College", "Snow College", "Southern Utah University", "Southwest Technical College", "Tooele Technical College", "Uintah Basin Technical College", "University of Utah", "Utah Education and Telehealth Network", "Utah State University", "Utah System of Higher Education", "Utah Tech University", "Utah Valley University", "Weber State University"
  ];

  const years = [2014, 2015, 2016, 2017, 2018, 2019, 2020, 2021, 2022, 2023, 2024];
  (window as any).$ = $;
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "/assets/styles/bootstrap_46.css";
    link.dataset.pageStyle = "bootstrap46";
    document.head.appendChild(link);

    const initLegacyFunctions = () => {
      const chartEl = document.getElementById("higher-ed-overtime-barchart");
      const otherEl = document.getElementById("all-spending-waffle");

      if (chartEl && otherEl) {
        try {
          initLegacyCharts();
        } catch (e) {
        }
        return true;
      }
      return false;
    };

    if (initLegacyFunctions()) return;

    const observer = new MutationObserver(() => {
      if (initLegacyFunctions()) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  //  Updated Second useEffect for function implementations
  useEffect(() => {
    window.selectedEntities = selectedEntities;
    window.selectedYears = selectedYears;

    const entities = selectedEntities;
    const years = selectedYears.map(Number);

    window.build_expenditure_by_institution_barchart?.(entities, years);
    window.build_expenditure_by_institution_table?.(entities, years);
    window.build_fte_by_institution_barchart?.(entities, years);
    window.build_fte_table?.(entities, years);
    window.build_detail_expenditure_by_institution_table?.(entities, years);

    // Added to get build_institution_exp_barchart working right
    if (window.build_institution_exp_barchart) {
      window.build_institution_exp_barchart(entities, years);
    }

    // Added to get Detailed Budgeted Expenditure Breakdown chart always loaded
    if (window.build_detail_expenditure_by_institution_table) {
      window.build_detail_expenditure_by_institution_table(entities, years);
    }
  }, [selectedYears, selectedEntities]);

  return (
    <div>
      <div id="osa-tooltip"></div>
      <HeaderComponent />
      <FeatureNav />
      <BreadCrumbs />
      {loading ? <Loader /> : <></>}

      <main role="main">
        <div id="greeter-jumbotron" className="jumbotron 
        jumbotron-fluid text-light d-flex justify-content-center mb-0" style={{ backgroundImage: `url(${LectureImage})`, paddingTop: "2.5rem", paddingBottom: "2.5rem" }}>
          <div id="greeter-message" className="container px-3 px-md-4 text-center text-md-left">
            <h2 className="mt-2 mb-2 text-center text-white" style={{ fontWeight: "400" }}>How much does Utah spend on </h2>
            <h1 className="display-3 text-center mb-5">
              <span className="badge text-black" style={{ backgroundColor: "#ffc63a" }}>Higher Education?</span>
            </h1>
            <p className="lead mb-3 text-center text-white">
              Welcome! This report provides an in-depth look at Utah's higher education expenditure,
              built using
              <span className="font-weight-bold" style={{ fontWeight: "600" }}> the
                <span className="text-warning"> gold standard </span>
                of financial reporting: Annual
                Comprehensive Financial Reports
                (ACFRs) meticulously audited by the Office of the Utah State Auditor
              </span>.
              These reports provide the
              most accurate and trustworthy data available.
            </p>

            <p className="lead mb-5 text-center text-white">
              Explore this resource to uncover trends and gain a clear understanding of how public funds are invested in
              Utah's higher education system. We aim to foster a transparent and
              well-informed discussion about higher education in Utah.
            </p>

            <nav className="row mx-0 px-0" aria-label="Table of Contents">
              <div className="col-12 mb-3 text-center">
                <h4 className="text-white" style={{ fontWeight: "400", textTransform: "uppercase" }}>Table of Contents</h4>
              </div>

              <div className="col-12 col-sm-6 col-md-4">
                <a className="btn btn-primary btn-block mb-2" href="#section1" role="button">
                  <FontAwesomeIcon icon={faCircle1} className="fa-lg me-1" />
                  Contextualizing Expenditures
                </a>
              </div>

              <div className="col-12 col-sm-6 col-md-4">
                <a className="btn btn-primary btn-block mb-2" href="#section2" role="button">
                  <FontAwesomeIcon icon={faCircle2} className="fa-lg me-1" />
                  How was the Money Utilized? </a>
              </div>

              <div className="col-12 col-sm-6 col-md-4">
                <a className="btn btn-primary btn-block mb-2" href="#section-sources" role="button">
                  <FontAwesomeIcon icon={faCircle3} className="fa-lg me-1" />
                  Sources </a>
              </div>
            </nav>

          </div>
        </div>

        <section id="section1" className="jumbotron jumbotron-fluid text-orange50 d-flex justify-content-center my-0 py-3" style={{ backgroundColor: "var(--tu-muted-dark-grey)" }}>
          <div id="section1-message" className="container mx-1 mx-md-5 px-1 px-md-4 text-center text-md-left">
            <h2 className="mt-2 font-weight-bold h1">
              <span className="text-white">Chapter 1:</span> <span className="text-warning">Contextualizing Higher
                Education Expenditures
              </span>
            </h2>
          </div>
        </section>

        <section id="section-higher-ed-explained" className="jumbotron jumbotron-fluid text-primary d-flex bg-orange50 justify-content-center ">
          <div id="higher-ed-explained-content" className="container mx-1 mx-md-5 px-1 px-md-4 text-center">
            <h3 className="font-italic mb-3 mb-md-3 text-center text-md-left"><span
              style={{ color: "var(--tu-primary-yellow)" }}
            >Higher
              Education</span> refers to the 18
              Institutions that
              received State
              funding: </h3>
            <div className="card-deck">
              <article className="card bg-blue100">
                <div className="card-body">
                  <h5 className="card-title">
                    <FontAwesomeIcon icon={faGraduationCap} className="fa-2xl" />
                    8 Traditional</h5>
                  <hr />
                  <p className="card-text">
                  </p><ul className="text-left">
                    <li>University of Utah</li>
                    <li>Utah State University</li>
                    <li>Utah Valley University</li>
                    <li>Weber State University</li>
                    <li>Salt Lake Community College</li>
                    <li>Southern Utah University</li>
                    <li>Utah Tech University</li>
                    <li>Snow College</li>
                  </ul>
                  <p />
                </div>
              </article>

              <article className="card bg-blue100">
                <div className="card-body">
                  <h5 className="card-title">
                    <FontAwesomeIcon icon={faUserHelmetSafety} className="fa-2xl" />
                    8 Technical</h5>
                  <hr />
                  <p className="card-text">
                  </p><ul className="text-left">
                    <li>Mountainland Technical College</li>
                    <li>Davis Technical College</li>
                    <li>Ogden-Weber Technical College</li>
                    <li>Bridgerland Technical College</li>
                    <li>Dixie Technical College</li>
                    <li>Uintah Basin Technical College</li>
                    <li>Southwest Technical College</li>
                    <li>Tooele Technical College</li>
                  </ul>
                </div>
              </article>

              <article className="card bg-blue100">
                <div className="card-body">
                  <h5 className="card-title">
                    <FontAwesomeIcon icon={faBuildingColumns} className="fa-2xl" />
                    2 Support</h5>
                  <hr />
                  <p className="card-text">
                  </p><ul className="text-left">
                    <li>Utah System of Higher Education (USHE)</li>
                    <li>Utah Education and Telehealth Network (UETN)</li>
                  </ul>
                  <p />
                </div>
              </article>
            </div>
          </div>
        </section>

        <section className="marketing container mb-5">
          <div className="row featurette my-5">
            <div className="col-md-7 mb-4">
              <h2 className="display-4 font-weight-bold pt-1 pt-md-1 pt-xl-5 text-left mb-1 mb-md-5">
                <FontAwesomeIcon icon={faMessageQuestion} className="me-2 fa-sm" />
                How much money was spent on <span style={{ color: "#ffc63a" }}>Higher Education</span> in 2024?
              </h2>
              <p className="lead mt-3">
                In 2024, Utah's state government spent a total of $22.1 Billion.
                <strong><span style={{ color: "#ffc63a" }}> Higher Education </span></strong>
                made up $2.1
                Billion (9.4% of total Expenditures).
              </p>
              <p className="lead">
                This means that for every dollar spent, approximately 9 cents goes to Higher Education. The other 91% goes
                towards other government activities such as transportation, medicaid, and public safety.
              </p>
            </div>
            <div className="col-md-5">
              <div className="text-center mb-3">
                <h2>Utah Government Expenditures</h2>
              </div>
              <div id="all-spending-waffle-container" className="mb-3 text-center">
                <h6 className="text-muted">Where each
                  <FontAwesomeIcon icon={faCircleDollar} />
                  represents 1% of Total
                  Expenditure</h6>
                <hr className="mb-4" />
                <div id="all-spending-waffle-legend" className="text-center mb-2" />
                <div id="all-spending-waffle" />
              </div>
            </div>
          </div>

          <section className="marketing container-fluid mx-0 px-0 mb-4 rounded border shadow-sm" style={{ backgroundColor: "var(--tu-muted-dark-grey)" }}>
            <div className="row  text-light mx-1 mx-md-5 px-0 px-md-2">
              <div className="col-12 mt-3 mt-md-5 mb-4">
                <h2 className="text-center d-md-block mb-4 text-white">
                  <FontAwesomeIcon icon={faMessageQuestion} className="me-2 fa-sm" />
                  How has Higher Education Expenditure <span className="font-weight-bold">Changed Over
                    Time? </span>
                </h2>
                <hr className="bg-light" />
              </div>
              <div className="col-12 px-2 px-md-2 mb-5" id="higher-ed-overtime-barchart-container">
                <h3 className="text-center text-white">Higher Education Expenditure 2004-2024</h3>
                <hr />
                <div id="higher-ed-overtime-barchart" className="text-light" />
                {/* Info Card */}
                <div className="card alert alert-warning mt-3">
                  <div className="row h-100 no-gutters p-2">
                    <h6 className="font-weight-bold mb-2">
                      <FontAwesomeIcon icon={faCircleInfo} className="fa-xl" />
                      Utah Higher Ed Expenditures Have Grown
                    </h6>
                    <div className="row">
                      <p className="lead" style={{ paddingLeft: "0", paddingRight: "0" }}>
                        A twenty year history shows that Higher Education Expenditures have trended upwards with
                        larger rates of increase occurring in recent years.
                      </p>
                      <ul className="lead" style={{ paddingLeft: "0", paddingRight: "0" }}>
                        <li><span className="font-weight-bold">Change from Last Year: </span> Increased $355 Million
                          (20.4%)
                          over a one year period (from $1.74 to $2.1 Billion).</li>
                        <li><span className="font-weight-bold">Five Year Change: </span> Increased $786 Million
                          (59.9%)
                          over a five year period (from $1.3 to $2.1 Billion).</li>
                        <li><span className="font-weight-bold">Ten Year Change: </span> Increased $1.191 Billion
                          (131.02%)
                          over a 10 year period (from $0.909 to $2.1 Billion).</li>
                        <li><span className="font-weight-bold">Twenty Year Change: </span> Increased $1.319 Billion
                          (168.89%)
                          over a 20 year period (from $0.781 to $2.1 Billion).</li>
                      </ul>
                    </div>
                  </div>
                </div>
                {/* Info Card */}
              </div>
              <div className="col-12 px-2 px-md-2 mb-5" id="higher-ed-overtime-perc-budget-container">
                <h3 className="text-center text-white">Percentage of Utah's Total Expenditure</h3>
                <hr />
                <div id="higher-ed-overtime-perc-budget" className="text-light" />
                {/* Info Card */}
                <div className="card alert alert-warning mt-3" style={{
                  borderColor: "#fff4d8",
                  color: "var(--tu-alert-brown)",
                  backgroundColor: "#fff4d8"
                }}>
                  <div className="row h-100 no-gutters p-2">
                    <h6 className="font-weight-bold mb-2">
                      <FontAwesomeIcon icon={faCircleInfo} className="fa-xl" />
                      Percentage of Total Expenditures
                    </h6>
                    <div className="row">
                      <p className="lead" style={{ paddingLeft: "0", paddingRight: "0" }}>
                        While increased spending can be concerning, it is important to note that this
                        growth aligns with overall trends in state Expenditure. Higher Education consistently
                        accounts for 8-11% of the state
                        budget, indicating the growth mirrors overall state
                        budget increases.
                      </p>
                      <ul className="lead" style={{ paddingLeft: "0", paddingRight: "0" }}>
                        <li><span className="font-weight-bold">Five Year Average: </span> 8.6%</li>
                        <li><span className="font-weight-bold">Ten Year Average: </span> 8.9%</li>
                        <li><span className="font-weight-bold">Twenty Year Average: </span> 9.3%</li>
                      </ul>
                    </div>
                  </div>
                </div>
                {/* Info Card */}
              </div>
            </div>
          </section>

          <section className="container">
            <h2 className="featurette-heading pt-1 pt-md-4 pt-xl-5 mb-4">
              <FontAwesomeIcon icon={faMessageQuestion} className="me-2 fa-sm" style={{ color: "#4a5568" }} />
              How does <span style={{ color: "#ffc63a" }}>
                Higher Education </span> Expenditure Growth Compare to other
              Government
              Activities?
            </h2>
            <div className="alert alert-warning">
              <h4 className="mb-3">
                <FontAwesomeIcon icon={faSquareQuestion} className="fa-xl" />
                What is a government activity?
              </h4>
              <p className="lead">Imagine the State of Utah as a Big Company.
                Instead of selling products like a business, the State of Utah provides services and programs to its
                citizens. These include things like:
              </p>
              <ul className="lead">
                <li>Education: Funding for public schools and universities.</li>
                <li>Healthcare: Medicaid, public health programs.</li>
                <li>Transportation: Building and maintaining roads and highways.</li>
                <li>Public Safety: Funding police, fire departments, etc.</li>
                <li>Parks and Recreation: Maintaining state parks.</li>
              </ul>
              <p className="lead">
                We use the "Statement of Activities," which is a report card that shows how much money the state brought
                in
                (revenue) and
                how
                much it spent (expenses) on all these services, for the table below:</p>
            </div>
            <div className="alert alert-primary">

              <h3 className="font-weight-bold mb-2">Activity Expenditure Highlight Table</h3>
              <p className="lead">
                To help understand Higher Education's growth relative to other government activities, we will use a
                Highlight
                Table to indicate High/Low Expenditure using a color gradient.
              </p><h5>2024 Expenditure and Percent of Budget Legend:</h5>
              <div id="highlight-table-legend" />
              <h5 className="mt-2">Change in Expenditure:</h5>
              <div id="highlight-table-legend2" />
              <p />
            </div>
            <div id="all-activities-heatmap-table-warning" className="alert alert-warning">The highlight table does not work
              well on small screens. Please turn your device sideways or use a larger screen to see the colors.</div>
            <div id="all-activities-heatmap-table" className="table-sm" />

          </section>
        </section>

        <section id="section2" className="jumbotron jumbotron-fluid text-orange50 d-flex  justify-content-center my-0 py-3" style={{ backgroundColor: "var(--tu-muted-dark-grey)" }}>
          <div id="section2-message" className="container mx-1 mx-md-5 px-1 px-md-4 text-center text-md-left">
            <h2 className="mt-2 font-weight-bold h1">
              <span className="text-white">Chapter 2:</span> <span className="text-warning">How was the Money Utilized?
              </span>
            </h2>
          </div>
        </section>

        {/* START THE FEATURETTES */}
        <section className="marketing container-fluid mb-5">
          <section className="row featurette mt-4 mx-2">
            <div className="col-12 text-center mx-0 mx-md-2 px-0 px-md-2">
              <h2 className="featurette-heading pt-1 pt-md-4 pt-xl-5 mb-4 font-weight-bold">
                <FontAwesomeIcon icon={faMessageQuestion} className="me-2 fa-sm" />
                Monies received by each Institution
              </h2>
            </div>
            <div className="col-12 col-md-4 mx-0 mx-md-2 px-0  mb-3">
              <div id="state-expenditures-table" className="table-secondary table-sm" />
            </div>
            <div className="col-12 col-md-7 mx-0 mx-md-2 px-0" id="state-expenditures-barchart-container">
              <h3 className="text-center">2024 State Expenditures by Institution</h3>
              <hr />
              <div id="state-expenditures-barchart" className="text-light" />
              <div className="text-center"><small>Source: <a href="https://finance.utah.gov/wp-content/uploads/FY24-ACFR-Final.pdf" target="_blank" rel="noopener noreferrer">State of Utah Annual Comprehensive Financial Report -
                <i>"Detail Schedule of
                  Expenditures – Budget and Actual" (pg 190-191)</i></a> </small></div>
            </div>
          </section>

          <div>
            {/* Info Card */}
            <div className="card alert mt-3" style={{ border: "0", color: "var(--tu-alert-brown)", backgroundColor: "#fff4d8" }}>
              <div className="row h-100 no-gutters p-2">
                <h6 className="font-weight-bold mb-2" style={{ color: "var(--tu-alert-brown)" }}>
                  <FontAwesomeIcon icon={faCircleInfo} className="fa-xl" />
                  Traditional Universities (and Supporting Institutions) receive more than Technical Colleges
                </h6>
                <div>
                  <p className="lead">The top ten highest Expenditures all went to traditional institutions or support
                    institutions.</p>
                  <ul className="lead">
                    <li> <span className="font-weight-bold"> Highest Traditional: </span>The University of Utah was appropriated
                      the most at $605 Million.</li>
                    <li> <span className="font-weight-bold"> Lowest Traditional: </span>Snow College's $47 Million was the smallest of
                      traditional institutions</li>
                    <li> <span className="font-weight-bold"> Highest Technical: </span>Mountainland Technical College had the
                      highest for technical colleges with $28 Million.</li>
                    <li> <span className="font-weight-bold"> Lowest Technical: </span>Tooele
                      Technical College was appropriated the least at $8.6 Million.</li>
                  </ul>
                </div>
              </div>
            </div>
            {/* Info Card */}
          </div>

          <hr className="mt-4" />

          <section className="row featurette mt-4 mx-2 mb-5">
            <div className="col-12 text-center">
              <h2 className="featurette-heading pt-1 pt-md-4 pt-xl-5 mb-4 font-weight-bold">
                <FontAwesomeIcon icon={faMessageQuestion} className="me-2 fa-sm" />
                How has each instutition changed over time?
              </h2>
            </div>

            <div className="col-12 mb-5">
              <div className="row alert alert-info border shadow rounded py-4">
                <div className="col-12">
                  <p className="lead">
                    <FontAwesomeIcon icon={faCircleInfo} size="xl" className="me-2" />
                    Toggle between Institutions of
                    Higher
                    Education and Fiscal Years of interest to
                    update the charts and tables.
                  </p>
                </div>

                <div className="col-12 col-md-6 mb-2">
                  <h6>Select Institution of Higher Education</h6>
                  <SingleSelect
                    options={technicalColleges.map(college => ({
                      value: college,
                      label: college
                    }))}
                    value={
                      selectedEntities.length > 0
                        ? { value: selectedEntities[0], label: selectedEntities[0] }
                        : null
                    }

                    onChange={(selectedOption) => {
                      setSelectedEntities(selectedOption ? [selectedOption.value] : []);
                    }}

                    isSearchable={true}
                  />
                </div>

                <div className="col-12 col-md-6 mb-2">
                  <h6>Select Fiscal Years (2014-2024)</h6>

                  <MultiSelectNoExtras
                    options={years.map(year => ({
                      value: year.toString(),
                      label: year.toString()
                    }))}
                    value={selectedYears.map(year => ({
                      value: year,
                      label: year
                    }))}
                    onChange={(selectedOptions) => {
                      const values = selectedOptions ? selectedOptions.map(option => option.value) : [];
                      setSelectedYears(values);
                    }}

                    isSearchable={true}
                    closeMenuOnSelect={false}
                  />
                </div>
              </div>
            </div>

            <hr />

            <div className="col-12 col-md-6 mb-5" id="institution-exp-section">
              <h3>Budgeted Expenditure Over Time</h3>
              <hr />
              <div id="institution-bar-chart-container">
                <div id="institution-bar-chart" />
                {selectedYears ? <div>Selected Years: {selectedYears}</div> : <div>No selected Years</div>}
              </div>

              <div id="institution-exp-table" />
            </div>

            <div className="col-12 col-md-6 mb-5" id="institution-fte-section">
              <h3>Budget-Related Annualized FTE</h3>
              <hr />

              <div id="institution-fte-bar-chart-container">
                <div id="institution-fte-bar-chart" />
              </div>

              <div id="institution-fte-table" />
            </div>
            <div className="col-12 mb-5">
              <h5 className="mb-3">Detailed Budgeted Expenditure Breakdown</h5>
              <div id="institution-detail-exp-table" />
            </div>
          </section>
        </section>

        <section id="section-sources" className="jumbotron jumbotron-fluid text-orange50 d-flex justify-content-center py-3" style={{ backgroundColor: "var(--tu-muted-dark-grey)" }}>
          <div id="section3-message" className="container mx-1 mx-md-5 px-1 px-md-4 text-center text-md-left">
            <h2 className="mt-2 font-weight-bold h1" style={{ fontSize: "3.5rem" }}>
              <span className="text-white">Chapter 3: </span> <span className="text-warning">Sources</span>
            </h2>
          </div>
        </section>

        <section id="section-sources-container" className="container">
          <h3 id="source-section1" className="lead font-weight-bold">
            Annual Comprehensive Financial Reports:
          </h3>
          <p className="lead">
            We utilizied data found in the "Statement of Activities" and the "Detail Schedule of Expenditures – Budget and
            Actual" financial statements.
          </p>

          <div id="sources1">
          </div>

          <h3 id="source-section2" className="lead my-3 font-weight-bold">
            Budget-Related Annualized Full Time Enrollment (FTE)
          </h3>

          <div id="sources2">
            <ul className="lead mt-2">
              <li>
                <a
                  href="https://ushe.edu/wp-content/uploads/pdf/databook/2025/Budget_History_2025.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-wrap-text p-2 text-white d-inline-flex align-items-center"
                  style={{ backgroundColor: "var(--tu-muted-dark-grey)" }}
                >
                  <FontAwesomeIcon icon={faFilePdf} className="me-2 text-white" />
                  History Data Book - Table 6
                </a>
              </li>
            </ul>
          </div>
        </section>

        <div className="container alert alert-info">To find more information about higher education Higher Education Cash and Cash Equivalents,
          please visit our <a href="https://transparent.utah.gov//he-cash/" target="_blank" rel="noopener noreferrer">"Higher Education
            Cash Flows" dashboard</a>
        </div>
      </main>
      <FooterSection />
    </div>
  )
}

export default HigherEducation;